import { OpenInNewOutlined } from "@mui/icons-material";
import { Box, Chip, Pagination, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContainerCard } from "../../../../components/ContainerCard";
import { Tooltip } from "../../../../components/Tooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { hasDatePassed } from "../../../../shared/utils/date";
import { Routes } from "../../../../types/NavigationPaths";

export interface LicenseChipItem {
  endDate?: Date;
  id: string;
  label: string;
  startDate?: Date;
}

const CHIP_ROWS_PER_PAGE = 8;
const CHIP_GAP = 8;
const CHIP_FONT_WEIGHT = 500;
const CHIP_BORDER_WIDTH = 2;
const CHIP_ICON_GAP = 4;
const CHIP_WIDTH_SAFETY = 4;
const FALLBACK_CHAR_WIDTH = 7.5;
const FALLBACK_CONTAINER_WIDTH = 1000;

interface ChipSize {
  fontSize: number;
  height: number;
  iconSize: number;
  paddingX: number;
}

const DESKTOP_CHIP_SIZE: ChipSize = {
  fontSize: 13,
  height: 34,
  iconSize: 13,
  paddingX: 12,
};
const MOBILE_CHIP_SIZE: ChipSize = {
  fontSize: 11,
  height: 26,
  iconSize: 11,
  paddingX: 8,
};

interface LicenseChipsProps {
  licenses: LicenseChipItem[];
}

const getValidityText = (
  t: ReturnType<typeof useLocalization>["t"],
  startDate?: Date,
  endDate?: Date,
): string | undefined => {
  if (endDate) {
    return t("pages.dataAnalytics.externalUsage.licenseLink.validFromTo", {
      end: formatDate(endDate),
      start: formatDate(startDate),
    });
  }
  if (startDate) {
    return t("pages.dataAnalytics.externalUsage.licenseLink.validFrom", {
      start: formatDate(startDate),
    });
  }

  return undefined;
};

const formatDate = (date?: Date): string => {
  if (!date) return "";
  const day = date.getUTCDate();
  const month = date.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};

let measureContext: CanvasRenderingContext2D | null | undefined;

const measureTextWidth = (text: string, font: string): number => {
  if (measureContext === undefined) {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  if (!measureContext) return text.length * FALLBACK_CHAR_WIDTH;

  measureContext.font = font;

  return measureContext.measureText(text).width;
};

export const getChipPageStarts = (
  chipWidths: number[],
  containerWidth: number,
  rowsPerPage: number,
): number[] => {
  const pageStarts = [0];
  let row = 1;
  let rowWidth = 0;

  chipWidths.forEach((chipWidth, index) => {
    const width = Math.min(chipWidth, containerWidth);
    const widthWithChip = rowWidth === 0 ? width : rowWidth + CHIP_GAP + width;
    if (widthWithChip <= containerWidth) {
      rowWidth = widthWithChip;

      return;
    }

    row += 1;
    rowWidth = width;
    if (row > rowsPerPage) {
      pageStarts.push(index);
      row = 1;
    }
  });

  return pageStarts;
};

export const LicenseChips: FC<LicenseChipsProps> = ({ licenses }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { t } = useLocalization();
  const isMobile = useIsMobile();
  const chipSize = isMobile ? MOBILE_CHIP_SIZE : DESKTOP_CHIP_SIZE;
  const [page, setPage] = useState(1);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => setPage(1), [licenses]);

  const hasLicenses = licenses.length > 0;

  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;

    setContainerWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    observer.observe(el);

    return () => observer.disconnect();
  }, [hasLicenses]);

  useEffect(() => {
    if (!("fonts" in document)) return;

    let isMounted = true;
    void document.fonts.ready.then(() => {
      if (isMounted) setFontsLoaded(true);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const chipWidths = useMemo(() => {
    const font = `${CHIP_FONT_WEIGHT} ${chipSize.fontSize}px ${theme.typography.fontFamily}`;
    const chromeWidth = 2 * chipSize.paddingX + CHIP_BORDER_WIDTH;
    const iconWidth = CHIP_ICON_GAP + chipSize.iconSize;

    return licenses.map(({ endDate, label }) => {
      const isExpired = endDate ? hasDatePassed(endDate) : false;

      return (
        measureTextWidth(label, font) +
        chromeWidth +
        (isExpired ? 0 : iconWidth) +
        CHIP_WIDTH_SAFETY
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenses, theme.typography.fontFamily, chipSize, fontsLoaded]);

  const pageStarts = useMemo(
    () =>
      getChipPageStarts(
        chipWidths,
        containerWidth || FALLBACK_CONTAINER_WIDTH,
        CHIP_ROWS_PER_PAGE,
      ),
    [chipWidths, containerWidth],
  );

  const pageCount = pageStarts.length;
  const pagedChipsMinHeight =
    CHIP_ROWS_PER_PAGE * chipSize.height + (CHIP_ROWS_PER_PAGE - 1) * CHIP_GAP;
  const currentPage = Math.min(page, pageCount);
  const visibleLicenses = licenses.slice(
    pageStarts[currentPage - 1],
    pageStarts[currentPage] ?? licenses.length,
  );

  return (
    <ContainerCard
      title={t("pages.dataAnalytics.externalUsage.selectedLicenses")}
    >
      {licenses.length === 0 ? (
        <Typography
          sx={{
            color: theme.colors.text[1],
            fontSize: 14,
            textAlign: "center",
            width: "100%",
          }}
        >
          {t("pages.dataAnalytics.externalUsage.noLicensesSelected")}
        </Typography>
      ) : (
        <Box
          data-testid="license-chips"
          ref={chipsRef}
          sx={{
            alignContent: "flex-start",
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            minHeight: pageCount > 1 ? pagedChipsMinHeight : undefined,
            "& > *": { maxWidth: "100%", minWidth: 0 },
          }}
        >
          {visibleLicenses.map(({ endDate, id, label, startDate }) => {
            const isExpired = endDate ? hasDatePassed(endDate) : false;
            const validityText = getValidityText(t, startDate, endDate);

            return (
              <Tooltip key={id} message={validityText ?? label} placement="top">
                <Chip
                  label={
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: `${CHIP_ICON_GAP}px`,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: chipSize.fontSize,
                          fontWeight: CHIP_FONT_WEIGHT,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                      {!isExpired && (
                        <OpenInNewOutlined
                          sx={{ flexShrink: 0, fontSize: chipSize.iconSize }}
                        />
                      )}
                    </Box>
                  }
                  onClick={
                    isExpired
                      ? undefined
                      : () => void navigate(`/companies/${companyId}${Routes.EXTERNAL_LICENSES}/${id}`)
                  }
                  sx={{
                    bgcolor: isExpired
                      ? theme.colors.secondary[1]
                      : theme.colors.secondary[0],
                    border: `1px solid ${isExpired ? theme.colors.secondary[2] : theme.colors.secondary[1]}`,
                    borderRadius: 2,
                    color: isExpired
                      ? theme.colors.text[0]
                      : theme.colors.text[5],
                    height: chipSize.height,
                    maxWidth: "100%",
                    "& .MuiChip-label": { px: `${chipSize.paddingX}px` },
                    ...(isExpired
                      ? { opacity: 0.75 }
                      : {
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: theme.colors.secondary[1],
                          borderColor: theme.colors.secondary[3],
                        },
                      }),
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}
      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          onChange={(_, value) => setPage(value)}
          page={currentPage}
          size="small"
          sx={{ alignSelf: "center", mt: 1 }}
        />
      )}
    </ContainerCard>
  );
};
